import React, { useEffect } from 'react';
import { StyleSheet } from 'react-native';
import { Container, Box, Typography, Stack } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import ExternalAccountTrigger from '../../components/ExternalAccount/ExternalAccountTrigger';
import ExternalAccountList from '../../components/ExternalAccount/ExternalAccountList';
import { getUserExternalAccounts } from '../../redux/account/actions';
import { RootState } from '../../redux/root-reducer';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import CustomButton from '../../components/Button/CustomButton';
import AddIcon from '../../components/CustomSVGIcons/AddIcon';

const styles = StyleSheet.create({
    container: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
    },
});

const AccountExternalBankAccounts = () => {
    const dispatch = useDispatch();
    const { themeStyle } = useThemeContext();
    const t = theme[themeStyle];
    const { userExternalAccounts, gettingUserExternalAccounts } = useSelector((state: RootState) => state.account);
    const [showAddModal, setShowAddModal] = React.useState(false);
    const maxAccounts = 3;

    useEffect(() => {
        dispatch(getUserExternalAccounts());
    }, [dispatch]);

    const handleAddAccount = () => {
        setShowAddModal(true);
    };

    const handleCloseModal = () => {
        setShowAddModal(false);
    };

    const addAccountButtonProps = {
        sx: {
            border: 'none',
            backgroundColor: 'transparent',
            alignSelf: 'flex-end',
            mr: 1,
            px: 1,
            py: 0.5,
            minWidth: 0,
            '&:hover': {
                backgroundColor: 'transparent',
                border: 'none',
                color: t.primary_white,
                WebkitTextFillColor: t.primary_white,
                '& .billing-add-card-icon': {
                    transform: 'rotate(90deg)',
                },
                '& .MuiTypography-root': {
                    color: t.primary_white,
                    WebkitTextFillColor: t.primary_white,
                },
            },
        },
        textStyle: {
            fontSize: '1rem',
            fontWeight: '800',
            letterSpacing: '0.08em',
            fontFamily: FONT_ENUM.MAIN_BOLD,
            color: t.highlight,
        },
        buttonProps: {
            variant: 'text' as const,
            sx: {
                border: 'none',
            },
        },
    };

    return (
        <Container disableGutters style={styles.container} maxWidth={false}>
            <Box sx={{ width: '100%', maxWidth: 1200, p: 3 }}>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'stretch', sm: 'flex-start' }}
                    spacing={2}
                    sx={{ mb: 3 }}>
                    <Typography
                        sx={{
                            fontSize: '0.875rem',
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            fontWeight: 500,
                            color: t.tag_white,
                            opacity: 0.75,
                            lineHeight: 1.5,
                            maxWidth: '28rem',
                            flex: 1,
                            minWidth: 0,
                        }}>
                        Manage your connected external liquidity pools and alternative settlement accounts for seamless
                        talent payouts.
                    </Typography>
                    <CustomButton
                        label="Add Account"
                        onClick={handleAddAccount}
                        disabled={Boolean(userExternalAccounts && userExternalAccounts.length >= maxAccounts)}
                        withIcon
                        beginningIcon={
                            <AddIcon
                                className="billing-add-card-icon"
                                style={{ marginRight: 8, transition: 'transform 0.2s ease' }}
                                height="22px"
                                width="22px"
                                fill={t.highlight}
                            />
                        }
                        sx={addAccountButtonProps.sx}
                        textStyle={addAccountButtonProps.textStyle}
                        buttonProps={addAccountButtonProps.buttonProps}
                    />
                </Stack>

                {userExternalAccounts && userExternalAccounts.length >= maxAccounts && (
                    <Box sx={{ mb: 2 }}>
                        <Typography
                            variant="body2"
                            sx={{
                                color: '#f44336',
                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                            }}>
                            Maximum 3 accounts reached. Please delete an existing account to add a new one.
                        </Typography>
                    </Box>
                )}

                {gettingUserExternalAccounts ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                        <Typography
                            variant="body1"
                            sx={{
                                color: t.light_gray_text,
                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                            }}>
                            Loading accounts...
                        </Typography>
                    </Box>
                ) : (
                    <ExternalAccountList accounts={userExternalAccounts || []} />
                )}

                <ExternalAccountTrigger
                    showPageLayout={userExternalAccounts && userExternalAccounts.length < 1}
                    open={showAddModal}
                    onClose={handleCloseModal}
                    hideButton={userExternalAccounts && userExternalAccounts.length > 1}
                />
            </Box>
        </Container>
    );
};

export { AccountExternalBankAccounts };
